/**
 * ai-member-form-dialog.tsx — Form KẾT NỐI nhân viên AI bên thứ ba / Webhook (theo mẫu SaleSmartly), dùng cho TẠO và SỬA:
 *
 *   - third_party  Bot dựng sẵn ở nền tảng: Trợ lý OpenAI (Coze / Dify / GPTBots chỉ còn cho bot cũ) — chung một ô "Nền tảng" với Webhook
 *                  → khoá/ID theo nền tảng + "Kiểm tra kết nối".
 *   - webhook      Agent tự dựng → URL nhận tin; eCDP cấp URL trả lời + mã truy cập
 *                  (mã chỉ hiện MỘT lần, "Tạo mã mới" để đổi).
 *
 * Trường chung: ảnh đại diện, tên nick (nội bộ), phân nhóm. Kênh phụ trách, giờ làm & chế độ, bật/tắt, số khách/ngày sửa ở
 * mục "Giờ làm & kênh" của màn nhân viên AI (`/ai/nhan-vien/:id`) — tạo xong form mở màn đó.
 * Nhân viên AI tích hợp sẵn KHÔNG dùng form này (luồng tạo mới `/ai/nhan-vien/moi` + màn nhân viên AI).
 * Phần thuần (kiểm tra + payload) nằm ở ai-member-form-lib.ts.
 */
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import {
  Check, Copy, ExternalLink, ImagePlus, KeyRound, Loader2, PlugZap, RefreshCw, Upload,
} from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import {
  aiKeys, createAiBot, rotateAiBotToken, testAiBotConnection, updateAiBot,
  useOrgTeams, type AiBot,
} from '@/hooks/use-ai'
import { useUploadProductImage } from '@/hooks/use-products'
import { useOnboardingReturn } from '@/hooks/use-onboarding-return'
import { avatarSrc } from '@/pages/settings/settings-utils'
import { aiErrorMessage } from '@/lib/ai-credits'
import { botPath } from '@/lib/ai-khung'
import { useAiModelsBySource } from '@/hooks/use-ai-credits'
import { i18n } from '@/lib/i18n'
import {
  buildPayload, cleanExternalConfig, initialFormState, isMasked, modelOptionsFor, NAME_MAX, NO_MODEL,
  platformOf, selectablePlatforms, validateForm, WEBHOOK_OPTION, type AiMemberFormState, type ConnectMode, type ThirdPartyKind,
} from './ai-member-form-lib'

const EMOJIS = ['🤖', '🧑‍💼', '👩‍💼', '💁', '🦊', '🐼', '🌟', '💬', '🛍️', '🎧', '📦', '🧠']

const MODE_TITLE: Record<ConnectMode, string> = {
  get third_party() { return i18n.t('team:aiMemberFormDialog.modeTitle.thirdParty') },
  get webhook() { return i18n.t('team:aiMemberFormDialog.modeTitle.webhook') },
}

/** Ô sao chép nhanh (URL trả lời, mã truy cập). */
function CopyField({ label, value, secret }: { label: string; value: string; secret?: boolean }) {
  const { t } = useTranslation('team')
  const [copied, setCopied] = useState(false)
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-2">
        <Input readOnly value={value} className={cn('font-mono text-xs', secret && 'tracking-tight')} onFocus={(e) => e.target.select()} />
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={t('aiMemberFormDialog.copyFieldLabel', { label })}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value)
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            } catch {
              toast.error(t('aiMemberFormDialog.copyFailed'))
            }
          }}
        >
          {copied ? <Check className="text-success" /> : <Copy />}
        </Button>
      </div>
    </div>
  )
}

function FieldError({ msg }: { msg?: string }) {
  return msg ? <p className="text-xs text-destructive">{msg}</p> : null
}

export function AiMemberFormDialog({
  open,
  mode,
  bot,
  onClose,
  canEdit = true,
}: {
  open: boolean
  /** Cách tạo đã chọn ở hộp chọn (bỏ qua khi sửa — lấy theo bot.kind). */
  mode: ConnectMode
  /** Có bot = sửa; null = tạo mới. */
  bot: AiBot | null
  onClose: () => void
  canEdit?: boolean
}) {
  const { t } = useTranslation('team')
  const navigate = useNavigate()
  // Mở từ trang Bắt đầu (bước 3 — Cấu hình Nhân viên AI) → báo xong bước + đường quay lại.
  const onboardingReturn = useOnboardingReturn()
  const qc = useQueryClient()
  const isEdit = !!bot
  const teams = useOrgTeams()
  const upload = useUploadProductImage()
  const fileRef = useRef<HTMLInputElement>(null)
  const pemRef = useRef<HTMLInputElement>(null)

  const [form, setForm] = useState<AiMemberFormState>(() => initialFormState(mode, bot))
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null)
  /** Mã truy cập bản rõ — chỉ có ngay sau khi tạo / tạo mã mới. */
  const [issued, setIssued] = useState<{ botId: string; token: string; replyUrl: string } | null>(null)
  const [rotating, setRotating] = useState(false)

  // Nạp lại state mỗi lần mở (theo bot/mode) — không đè khi đang gõ.
  const key = open ? `${bot?.id ?? 'new'}:${mode}` : null
  if (key !== loadedKey) {
    setLoadedKey(key)
    if (key) {
      setForm(initialFormState(mode, bot))
      setErrors({})
      setTestResult(null)
      setIssued(null)
    }
  }

  const set = (patch: Partial<AiMemberFormState>) => setForm((f) => ({ ...f, ...patch }))
  const setCfg = (k: string, v: string) => setForm((f) => ({ ...f, externalConfig: { ...f.externalConfig, [k]: v } }))

  const platform = platformOf(form.kind)
  // Ô Model của nền tảng ngoài chọn theo đúng danh mục "Key của tôi" (cùng nguồn với ô chọn model ở Nguồn AI).
  const needsModels = form.mode === 'third_party' && !!platform?.fields.some((f) => f.modelProvider)
  const byokModels = useAiModelsBySource(needsModels ? 'byok' : null)

  function close() {
    setIssued(null)
    onClose()
  }
  /** Tạo xong → màn nhân viên AI, mục "Giờ làm & kênh" để giao kênh. */
  function closeAndOpen(id: string) {
    close()
    navigate(onboardingReturn.carry(`${botPath(id)}#gio-lam`, 3))
  }

  async function onUpload(file: File) {
    if (file.size > 5 * 1024 * 1024) return toast.error(t('aiMemberFormDialog.imageTooLarge'))
    try {
      const url = await upload.mutateAsync(file)
      set({ avatarUrl: url })
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function onPem(file: File) {
    if (file.size > 64 * 1024) return toast.error(t('aiMemberFormDialog.pemTooLarge'))
    const text = await file.text()
    if (!/-----BEGIN (RSA )?PRIVATE KEY-----/.test(text)) return toast.error(t('aiMemberFormDialog.pemNotPrivateKey'))
    setCfg('privateKey', text.trim())
  }

  async function testConnection() {
    setTesting(true)
    setTestResult(null)
    try {
      const res = await testAiBotConnection({
        botId: bot?.id,
        kind: form.kind,
        externalConfig: cleanExternalConfig(form.kind, form.externalConfig),
      })
      setTestResult(res.ok
        ? { ok: true, text: t('aiMemberFormDialog.testSuccess', { seconds: (res.latencyMs / 1000).toFixed(1), reply: res.reply }) }
        : { ok: false, text: res.error || t('aiMemberFormDialog.testFailed') })
    } catch (err) {
      setTestResult({ ok: false, text: apiError(err) })
    } finally {
      setTesting(false)
    }
  }

  async function rotate() {
    if (!bot) return
    if (!window.confirm(t('aiMemberFormDialog.confirmRotateToken'))) return
    setRotating(true)
    try {
      const res = await rotateAiBotToken(bot.id)
      setIssued({ botId: bot.id, token: res.token, replyUrl: res.replyUrl })
      await qc.invalidateQueries({ queryKey: aiKeys.bots })
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setRotating(false)
    }
  }

  async function save() {
    const errs = validateForm(form, isEdit)
    setErrors(errs)
    if (Object.keys(errs).length) return
    setSaving(true)
    try {
      const payload = buildPayload(form)
      if (isEdit && bot) {
        const upd = await updateAiBot(bot.id, payload)
        await qc.invalidateQueries({ queryKey: aiKeys.bots })
        toast.success(t('aiMemberFormDialog.saved'))
        for (const w of upd.warnings ?? []) toast.warning(w)
        close()
        onboardingReturn.complete(3)
        return
      }
      const res = await createAiBot({ ...payload, enabled: true })
      await qc.invalidateQueries({ queryKey: aiKeys.bots })
      for (const w of res.warnings ?? []) toast.warning(w)
      if (form.mode === 'webhook' && res.webhookToken) {
        // Giữ hộp mở để hiện URL trả lời + mã truy cập (chỉ lần này) — không tự chuyển trang.
        setIssued({ botId: res.bot.id, token: res.webhookToken, replyUrl: res.bot.webhookReplyUrl ?? '' })
        onboardingReturn.complete(3, { autoReturn: false })
      } else {
        toast.success(t('aiMemberFormDialog.connectedExternal'))
        onboardingReturn.complete(3, { autoReturn: false })
        closeAndOpen(res.bot.id)
      }
    } catch (err) {
      toast.error(aiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  // ── Màn hiện mã truy cập (sau khi tạo webhook / tạo mã mới) ─────────────
  if (issued && !isEdit) {
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) close() }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" /> {t('aiMemberFormDialog.webhookInfoTitle')}</DialogTitle>
            <DialogDescription>
              <Trans t={t} i18nKey="aiMemberFormDialog.saveTokenNowWarning" components={{ strong: <strong /> }} />
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <CopyField label={t('aiMemberFormDialog.replyUrlLabel')} value={issued.replyUrl} />
            <CopyField label={t('aiMemberFormDialog.accessTokenLabel')} value={issued.token} secret />
            <p className="text-xs text-muted-foreground">
              <Trans t={t} i18nKey="aiMemberFormDialog.webhookCallHint" components={{ code: <code /> }} />
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => closeAndOpen(issued.botId)}>{t('aiMemberFormDialog.savedTokenAssignChannel')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close() }}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? t('aiMemberFormDialog.editConnectionInfo') : t('aiMemberFormDialog.connectExternalBot')}
            <Badge variant="secondary" className="font-normal">{MODE_TITLE[form.mode]}</Badge>
          </DialogTitle>
          <DialogDescription>
            {t('aiMemberFormDialog.externalBotDesc')}
            {' '}{form.mode === 'webhook' ? t('aiMemberFormDialog.webhookDesc') : t('aiMemberFormDialog.thirdPartyDesc')}
          </DialogDescription>
        </DialogHeader>

        <fieldset disabled={!canEdit} className="space-y-5">
          {/* Ảnh đại diện */}
          <div className="flex items-start gap-4">
            <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-violet-500/10 text-3xl">
              {form.avatarUrl ? (
                <img src={avatarSrc(form.avatarUrl)} alt="" className="h-full w-full object-cover" />
              ) : (
                form.avatarEmoji || '🤖'
              )}
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={upload.isPending}>
                  {upload.isPending ? <Loader2 className="animate-spin" /> : <ImagePlus />} {t('aiMemberFormDialog.uploadImage')}
                </Button>
                {form.avatarUrl && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => set({ avatarUrl: '' })}>{t('aiMemberFormDialog.removeImage')}</Button>
                )}
                <span className="text-xs text-muted-foreground">{t('aiMemberFormDialog.imageHint')}</span>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void onUpload(f); e.target.value = '' }}
                />
              </div>
              <div className="flex flex-wrap gap-1">
                {EMOJIS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => set({ avatarEmoji: e, avatarUrl: '' })}
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-md border text-lg transition-colors hover:bg-accent',
                      !form.avatarUrl && form.avatarEmoji === e ? 'border-primary bg-primary/10' : 'border-transparent',
                    )}
                    aria-label={t('aiMemberFormDialog.chooseEmoji', { emoji: e })}
                  >
                    {e}
                  </button>
                ))}
              </div>
              <FieldError msg={errors.avatarUrl} />
            </div>
          </div>

          {/* Tên nick + phân nhóm */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ai-nick">{t('aiMemberFormDialog.nicknameLabel')} <span className="text-destructive">*</span></Label>
              <Input
                id="ai-nick"
                value={form.name}
                maxLength={NAME_MAX}
                placeholder={t('aiMemberFormDialog.nicknamePlaceholder')}
                onChange={(e) => set({ name: e.target.value })}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t('aiMemberFormDialog.nicknameHint')}</span>
                <span className="tabular-nums">{form.name.length}/{NAME_MAX}</span>
              </div>
              <FieldError msg={errors.name} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('aiMemberFormDialog.teamGroupLabel')}</Label>
              <Select value={form.teamId ?? '__none__'} onValueChange={(v) => set({ teamId: v === '__none__' ? null : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('aiMemberFormDialog.noTeamGroup')}</SelectItem>
                  {(teams.data?.teams ?? []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ── Chọn nền tảng: Webhook (bot tự dựng) và các nền tảng bot chung MỘT ô ── */}
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="flex items-center gap-2"><PlugZap className="h-4 w-4 text-primary" /> {t('aiMemberFormDialog.choosePlatform')} <span className="text-destructive">*</span></Label>
              {form.mode === 'third_party' && platform && (
                <a href={platform.docUrl} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                  {t('aiMemberFormDialog.viewIntegrationGuide')} <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
            <Select
              value={form.mode === 'webhook' ? WEBHOOK_OPTION : form.kind}
              // Đang sửa: không đổi được giữa Webhook và nền tảng (backend chặn — hai cách tạo khác hẳn).
              disabled={isEdit && form.mode === 'webhook'}
              onValueChange={(v) => {
                if (v === WEBHOOK_OPTION) set({ mode: 'webhook' })
                else set({ mode: 'third_party', kind: v as ThirdPartyKind, externalConfig: v === bot?.kind ? { ...(bot?.externalConfig ?? {}) } : {} })
                setTestResult(null)
              }}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(!isEdit || form.mode === 'webhook') && <SelectItem value={WEBHOOK_OPTION}>{t('aiMemberFormDialog.webhookOptionLabel')}</SelectItem>}
                {(!isEdit || form.mode === 'third_party') && selectablePlatforms(bot?.kind).map((p) => (
                  <SelectItem key={p.kind} value={p.kind}>{p.legacy ? t('aiMemberFormDialog.legacyPlatformLabel', { label: p.label }) : p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* ── Phần riêng theo nền tảng ── */}
          {form.mode === 'third_party' && (
            <div className="space-y-3 rounded-xl border p-4">
              {platform?.note && <p className="text-xs text-warning">{platform.note}</p>}
              {bot?.externalConfigBroken && (
                <p className="text-xs text-destructive">{t('aiMemberFormDialog.configBroken')}</p>
              )}
              {platform?.fields.map((f) => {
                const v = form.externalConfig[f.key] ?? ''
                const err = errors[`cfg.${f.key}`]
                return (
                  <div key={f.key} className="space-y-1">
                    <Label className="text-xs">
                      {f.label} {f.required && <span className="text-destructive">*</span>}
                    </Label>
                    {f.modelProvider ? (
                      <Select value={v || NO_MODEL} onValueChange={(nv) => setCfg(f.key, nv === NO_MODEL ? '' : nv)}>
                        <SelectTrigger>
                          <SelectValue placeholder={byokModels.isLoading ? t('aiMemberFormDialog.loadingModels') : f.placeholder} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_MODEL}>{t('aiMemberFormDialog.noModelUsePromptId')}</SelectItem>
                          {modelOptionsFor(byokModels.data?.models, f.modelProvider, v).map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : f.pem ? (
                      <>
                        <Textarea
                          value={v}
                          rows={isMasked(v) ? 1 : 4}
                          placeholder={f.placeholder}
                          className="font-mono text-xs"
                          onFocus={() => { if (isMasked(v)) setCfg(f.key, '') }}
                          onChange={(e) => setCfg(f.key, e.target.value)}
                        />
                        <div className="flex items-center gap-2">
                          <Button type="button" variant="outline" size="sm" onClick={() => pemRef.current?.click()}>
                            <Upload /> {t('aiMemberFormDialog.uploadPemFile')}
                          </Button>
                          <span className="text-xs text-muted-foreground">{t('aiMemberFormDialog.pemFileHint')}</span>
                          <input
                            ref={pemRef}
                            type="file"
                            accept=".pem,.key,.txt"
                            className="hidden"
                            onChange={(e) => { const file = e.target.files?.[0]; if (file) void onPem(file); e.target.value = '' }}
                          />
                        </div>
                      </>
                    ) : (
                      <Input
                        type={f.secret ? 'password' : 'text'}
                        autoComplete="off"
                        value={v}
                        placeholder={f.secret && isEdit ? t('aiMemberFormDialog.leaveBlankKeepKey') : f.placeholder}
                        onFocus={() => { if (f.secret && isMasked(v)) setCfg(f.key, '') }}
                        onChange={(e) => setCfg(f.key, e.target.value)}
                      />
                    )}
                    {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
                    <FieldError msg={err} />
                  </div>
                )
              })}
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <Button type="button" variant="outline" size="sm" onClick={() => void testConnection()} disabled={testing}>
                  {testing ? <Loader2 className="animate-spin" /> : <PlugZap />} {t('aiMemberFormDialog.testConnection')}
                </Button>
                <span className="text-xs text-muted-foreground">{t('aiMemberFormDialog.testConnectionHint')}</span>
              </div>
              {testResult && (
                <p className={cn(
                  'whitespace-pre-wrap rounded-lg px-3 py-2 text-xs',
                  testResult.ok ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive',
                )}>
                  {testResult.text}
                </p>
              )}
            </div>
          )}

          {form.mode === 'webhook' && (
            <div className="space-y-3 rounded-xl border p-4">
              <div className="space-y-1">
                <Label htmlFor="ai-hook">{t('aiMemberFormDialog.receiveUrlLabel')} <span className="text-destructive">*</span></Label>
                <Input
                  id="ai-hook"
                  value={form.webhookReceiveUrl}
                  placeholder="https://agent.cua-ban.vn/ecdp/webhook"
                  onChange={(e) => set({ webhookReceiveUrl: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  {t('aiMemberFormDialog.receiveUrlHint')}
                </p>
                <FieldError msg={errors.webhookReceiveUrl} />
              </div>
              {isEdit && bot ? (
                <>
                  <CopyField label={t('aiMemberFormDialog.replyUrlLabel')} value={bot.webhookReplyUrl ?? ''} />
                  {issued?.botId === bot.id ? (
                    <div className="space-y-1">
                      <CopyField label={t('aiMemberFormDialog.newTokenLabel')} value={issued.token} secret />
                      <p className="text-xs text-warning">{t('aiMemberFormDialog.oldTokenExpired')}</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <Label className="text-xs">{t('aiMemberFormDialog.accessTokenLabel')}</Label>
                      <div className="flex items-center gap-2">
                        <Input readOnly value={bot.hasWebhookToken ? '••••••••••••••••••••' : t('aiMemberFormDialog.noTokenYet')} className="font-mono text-xs" />
                        <Button type="button" variant="outline" size="sm" onClick={() => void rotate()} disabled={rotating}>
                          {rotating ? <Loader2 className="animate-spin" /> : <RefreshCw />} {t('aiMemberFormDialog.createNewToken')}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">{t('aiMemberFormDialog.tokenShownOnceHint')}</p>
                    </div>
                  )}
                </>
              ) : (
                <p className="rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                  <Trans t={t} i18nKey="aiMemberFormDialog.afterCreateHint" components={{ strong: <strong />, code: <code /> }} />
                </p>
              )}
            </div>
          )}
        </fieldset>

        <DialogFooter>
          <Button variant="ghost" onClick={close}>{canEdit ? t('common:actions.cancel') : t('common:actions.close')}</Button>
          {canEdit && (
            <Button onClick={() => void save()} disabled={saving}>
              {saving && <Loader2 className="animate-spin" />}
              {isEdit ? t('common:actions.save') : t('aiMemberFormDialog.createAiMember')}
            </Button>
          )}
        </DialogFooter>

      </DialogContent>
    </Dialog>
  )
}
